import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useCompany, PageHeader, EmptyState, Money } from "@/components/Shared";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Plus, Loader2, Truck } from "lucide-react";

export default function Suppliers() {
  const { companyId, cur } = useCompany();
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", email: "", tax_id: "" });
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/suppliers", { params: { company_id: companyId } }).then(({ data }) => setItems(data.suppliers));
  useEffect(() => { if (companyId) load(); }, [companyId]);

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/suppliers", form, { params: { company_id: companyId } });
      toast.success("Fournisseur créé"); setOpen(false); setForm({ name: "", phone: "", email: "", tax_id: "" }); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setSaving(false); }
  };

  return (
    <div data-testid="suppliers-page">
      <PageHeader title="Fournisseurs" subtitle="Vos partenaires d'approvisionnement." action={
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="rounded-full" data-testid="new-supplier-button"><Plus className="w-4 h-4 mr-1" /> Nouveau fournisseur</Button></DialogTrigger>
          <DialogContent className="bg-popover">
            <DialogHeader><DialogTitle className="font-display">Nouveau fournisseur</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="space-y-1"><Label>Nom *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="supplier-name-input" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1"><Label>Téléphone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="supplier-phone-input" /></div>
                <div className="space-y-1"><Label>Email</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
              </div>
            </div>
            <DialogFooter><Button onClick={save} disabled={saving || !form.name} className="rounded-full" data-testid="save-supplier-button">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Enregistrer"}</Button></DialogFooter>
          </DialogContent>
        </Dialog>} />
      <Card className="rounded-md border-border shadow-sm overflow-hidden">
        {items === null ? <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
          : items.length === 0 ? <EmptyState title="Aucun fournisseur" />
          : <Table>
              <TableHeader><TableRow><TableHead>Fournisseur</TableHead><TableHead>Contact</TableHead><TableHead className="text-right">Dette</TableHead></TableRow></TableHeader>
              <TableBody>{items.map((s) => (
                <TableRow key={s.id} data-testid={`supplier-row-${s.id}`}>
                  <TableCell className="font-medium flex items-center gap-2"><Truck className="w-4 h-4 text-muted-foreground" />{s.name}</TableCell>
                  <TableCell className="text-muted-foreground">{s.phone || s.email || "—"}</TableCell>
                  <TableCell className="text-right"><Money value={s.balance} cur={cur} /></TableCell>
                </TableRow>))}
              </TableBody>
            </Table>}
      </Card>
    </div>
  );
}
